/**
 * Customer Summary Component
 *
 * Displays customer metrics and visualizations
 */

import { useTranslation } from "react-i18next";
import type { CustomerSummary as CustomerSummaryType } from "../types/overview-types";
import { CohortRetentionMatrix } from "./cohort-retention-matrix";
import { CustomerActivityChart } from "./customer-activity-chart";
import { CustomerLoyaltyChart } from "./customer-loyalty-chart";
import { MetricCard } from "./metric-card";
import { MetricCardWrapper } from "./metric-card-wrapper";
import { TopCustomersTable } from "./top-customers-table";

interface CustomerSummaryProps {
  data: CustomerSummaryType;
}

export function CustomerSummary({ data }: CustomerSummaryProps) {
  const { t } = useTranslation("reports");

  return (
    <div className="space-y-4">
      {/* Primary Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Basic: Total Customers */}
        <MetricCard
          metric={{ ...data.totalCustomers, label: t("metrics.totalCustomers.label") }}
          tooltip={t("metrics.totalCustomers.tooltip")}
        />

        {/* Basic: New Customers */}
        <MetricCard
          metric={{ ...data.newCustomers, label: t("metrics.newCustomers.label") }}
          tooltip={t("metrics.newCustomers.tooltip")}
        />

        {/* Premium: Returning Customers */}
        <MetricCardWrapper
          metricKey="customers.returningCustomers"
          metric={
            data.returningCustomers
              ? { ...data.returningCustomers, label: t("metrics.returningCustomers.label") }
              : undefined
          }
          label={t("metrics.returningCustomers.label")}
          tooltip={t("metrics.returningCustomers.tooltip")}
        />

        {/* Premium: Retention Rate */}
        <MetricCardWrapper
          metricKey="customers.retentionRate"
          metric={
            data.retentionRate
              ? { ...data.retentionRate, label: t("metrics.retentionRate.label") }
              : undefined
          }
          label={t("metrics.retentionRate.label")}
          tooltip={t("metrics.retentionRate.tooltip")}
        />
      </div>

      {/* Additional Metrics Section */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Premium: Average Visit Frequency */}
        <MetricCardWrapper
          metricKey="customers.averageVisitFrequency"
          metric={
            data.averageVisitFrequency
              ? { ...data.averageVisitFrequency, label: t("metrics.avgVisitFrequency.label") }
              : undefined
          }
          label={t("metrics.avgVisitFrequency.label")}
          tooltip={t("metrics.avgVisitFrequency.tooltip")}
        />

        {/* Premium: Average Days Between Visits */}
        <MetricCardWrapper
          metricKey="customers.averageDaysBetweenVisits"
          metric={
            data.averageDaysBetweenVisits
              ? {
                  ...data.averageDaysBetweenVisits,
                  label: t("metrics.avgDaysBetweenVisits.label"),
                }
              : undefined
          }
          label={t("metrics.avgDaysBetweenVisits.label")}
          tooltip={t("metrics.avgDaysBetweenVisits.tooltip")}
        />

        {/* Premium: Inactive Customers */}
        <MetricCardWrapper
          metricKey="customers.inactiveCustomers"
          metric={
            data.inactiveCustomers
              ? { ...data.inactiveCustomers, label: t("metrics.inactiveCustomers.label") }
              : undefined
          }
          label={t("metrics.inactiveCustomers.label")}
          tooltip={t("metrics.inactiveCustomers.tooltip")}
        />

        {/* Premium: Average Customer Tenure */}
        <MetricCardWrapper
          metricKey="customers.averageCustomerTenure"
          metric={
            data.averageCustomerTenure
              ? { ...data.averageCustomerTenure, label: t("metrics.avgCustomerTenure.label") }
              : undefined
          }
          label={t("metrics.avgCustomerTenure.label")}
          tooltip={t("metrics.avgCustomerTenure.tooltip")}
        />

        {/* Premium: Customer Growth Rate */}
        <MetricCardWrapper
          metricKey="customers.customerGrowthRate"
          metric={
            data.customerGrowthRate
              ? { ...data.customerGrowthRate, label: t("metrics.customerGrowthRate.label") }
              : undefined
          }
          label={t("metrics.customerGrowthRate.label")}
          tooltip={t("metrics.customerGrowthRate.tooltip")}
        />

        {/* Premium: Churn Rate */}
        <MetricCardWrapper
          metricKey="customers.churnRate"
          metric={
            data.churnRate ? { ...data.churnRate, label: t("metrics.churnRate.label") } : undefined
          }
          label={t("metrics.churnRate.label")}
          tooltip={t("metrics.churnRate.tooltip")}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CustomerActivityChart data={data.dailyCustomerActivity} />
        <CustomerLoyaltyChart
          current={data.loyaltyDistribution?.current ?? null}
          previous={data.loyaltyDistribution?.previous ?? null}
        />
      </div>

      {/* Cohort Retention Matrix */}
      <CohortRetentionMatrix data={data.cohortRetention} />

      {/* Top Customers */}
      <TopCustomersTable data={data.topCustomers} />
    </div>
  );
}
